<template>
  <div class="product-detail-page">
    <div v-if="loading" class="loading">
      <div class="spinner"></div>
      Loading product details...
    </div>
    
    <div v-if="error" class="error">
      {{ error }}
      <button @click="retryCurrentProduct" class="retry-button">Try Again</button>
    </div>
    
    <div v-if="!loading && !error && product" class="product-container">
      <!-- Desktop Layout -->
      <div class="desktop-layout">
        <!-- Seller Info -->
        <div class="seller-info-top">
          <div class="seller-details">
            <div class="seller-name-container">
              <router-link 
                :to="{ name: 'Seller', query: { userId: product.sellerId }}" 
                class="seller-link"
                v-if="product.sellerId"
              >
                <img 
                  :src="product.sellerImage" 
                  :alt="product.sellerName"
                  class="seller-avatar"
                  @error="handleSellerImageError"
                />
                <h3 class="seller-name">{{ product.sellerName }}</h3>
              </router-link>
            </div>
            <div class="seller-stats">
              <span>{{ cleanedSellerReviews }}</span>
            </div>
          </div>
        </div>

        <!-- Main Content Area -->
        <div class="product-content">
          <!-- Left Column - Images Gallery -->
          <div class="product-images">
            <div class="main-image" :class="{ zoomed: isZoomed, 'is-sold-out': isProductSoldOut && !isZoomed }">
              <div v-if="isProductSoldOut && !isZoomed" class="sold-out-badge">Sold Out</div>
              <button 
                class="nav-button prev" 
                @click.stop="previousImage" 
                :disabled="!canNavigatePrevious"
                v-if="product && product.images && product.images.length > 1"
              ><span>❮</span></button>
              <img 
                :src="mainImage" 
                alt="Product image" 
                @error="handleImageError"
                :key="currentImageIndex"
                @click="toggleZoom"
              >
              <button 
                class="nav-button next" 
                @click.stop="nextImage" 
                :disabled="!canNavigateNext"
                v-if="product && product.images && product.images.length > 1"
              ><span>❯</span></button>
              <button 
                v-if="isZoomed" 
                class="close-zoom"
                @click="toggleZoom"
              >×</button>
            </div>
            <div class="image-thumbnails" v-if="product.images && product.images.length > 1">
              <div 
                v-for="(image, index) in product.images" 
                :key="index" 
                class="thumbnail"
                :class="{ active: currentImageIndex === index }"
                @click="setCurrentImage(index)"
              >
                <img :src="image" alt="Thumbnail" @error="handleThumbnailError">
              </div>
            </div>
          </div>
          
          <!-- Right Column - Product Info -->
          <div class="product-info">
            <!-- Product Description with Title and Quick Info -->
            <div class="product-description">
              <div class="product-title-container">
                <h1 class="product-title" :class="{ expanded: isTitleExpanded }">{{ cleanedDescription }}</h1>
                <button class="expand-button" @click="toggleTitleExpand">
                  {{ isTitleExpanded ? 'Short' : 'Expand' }}
                </button>
              </div>
              <div class="product-quick-info">
                <div class="product-quick-info-left">
                  <div class="product-price">
                    <span v-if="product.originalPrice" class="original-price" style="text-decoration: line-through; color: #000; margin-right: 8px; font-size: 0.9em;">{{ product.originalPrice }}</span>
                    <span :class="{'final-price-red': !!product.originalPrice}" :style="product.originalPrice ? { color: 'red', fontWeight: 'bold' } : {}">{{ product.price }}</span>
                  </div>
                  <div class="product-condition">{{ product.condition || 'New' }}</div>
                  <div class="product-size" v-if="product.size">{{ product.size }}</div>
                </div>
                <button class="favourite-btn-inline favourite-btn-price" @click.stop.prevent="toggleFavouriteUrl(currentProductUrl)">
                  <i
                    v-if="isFavouritedUrl(currentProductUrl)"
                    class="fas fa-heart favourite-heart is-active"
                    :class="{ popping: poppingFavouriteUrl === currentProductUrl }"
                  ></i>
                  <i v-else class="far fa-heart favourite-heart"></i>
                </button>
              </div>
              
              <!-- Move action buttons here -->
              <div class="action-buttons">
                <button class="action-button" @click="openContactModal">Contact Us</button>
                <button class="action-button" @click="addToCart">Add to Cart</button><!-- TODO: restore :disabled="isProductSoldOut" -->
                <!--<button class="action-button buy-now">Buy Now</button>-->
              </div>

              <!-- Admin Price Override -->
              <div v-if="isAdmin" class="admin-price-panel" style="margin-top: 20px; padding: 10px; border: 1px dashed red; background: #fff0f0;">
                <h4 style="margin: 0 0 10px 0; color: #d32f2f;">Admin: Override Price</h4>
                <div style="display: flex; gap: 10px;">
                  <input v-model="newPriceInput" type="number" step="0.01" placeholder="Enter new numeric price" style="flex: 1; padding: 8px; border: 1px solid #ccc; border-radius: 4px;" />
                  <button @click="updatePrice" :disabled="isUpdatingPrice" style="background: #d32f2f; color: white; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer; font-weight: bold;">
                    {{ isUpdatingPrice ? 'Updating...' : 'Change Price' }}
                  </button>
                </div>
                <p style="margin: 5px 0 0; font-size: 0.8em; color: #666;">Note: Enter raw number. Currency symbol will be added automatically.</p>
              </div>
            </div>
            
            <!-- Product Details -->
            <div class="item-main-info--ExVwW2NW">
              <div class="labels--ndhPFgp8" v-if="parsedSpecs.length">
                <div class="item--qI9ENIfp" v-for="(spec, index) in parsedSpecs" :key="index">
                  <div class="label--ejJeaTRV">
                    {{ spec.label }}
                    <span class="symbol--DK_64UaK">:</span>
                  </div>
                  <div class="value--EyQBSInp">{{ spec.value }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Mobile Layout -->
      <div class="mobile-layout">
        <!-- 1. Seller Info -->
        <div class="seller-info-top">
          <div class="seller-details">
            <div class="seller-name-container">
              <router-link 
                :to="{ name: 'Seller', query: { userId: product.sellerId }}" 
                class="seller-link"
                v-if="product.sellerId"
              >
                <img 
                  :src="product.sellerImage" 
                  :alt="product.sellerName"
                  class="seller-avatar"
                  @error="handleSellerImageError"
                />
                <h3 class="seller-name">{{ product.sellerName }}</h3>
              </router-link>
            </div>
            <div class="seller-stats">
              <span>{{ cleanedSellerReviews }}</span>
            </div>
          </div>
        </div>

        <!-- 2. Product Images -->
        <div class="product-images">
          <div class="main-image" :class="{ zoomed: isZoomed, 'is-sold-out': isProductSoldOut && !isZoomed }">
            <div v-if="isProductSoldOut && !isZoomed" class="sold-out-badge">Sold Out</div>
            <button 
              class="nav-button prev" 
              @click.stop="previousImage" 
              :disabled="!canNavigatePrevious"
              v-if="product && product.images && product.images.length > 1"
            ><span>❮</span></button>
            <img 
              :src="mainImage" 
              alt="Product image" 
              @error="handleImageError"
              :key="currentImageIndex"
              @click="toggleZoom"
            >
            <button 
              class="nav-button next" 
              @click.stop="nextImage" 
              :disabled="!canNavigateNext"
              v-if="product && product.images && product.images.length > 1"
            ><span>❯</span></button>
            <button 
              v-if="isZoomed" 
              class="close-zoom"
              @click="toggleZoom"
            >×</button>
          </div>
          <div class="image-thumbnails" v-if="product.images && product.images.length > 1">
            <div 
              v-for="(image, index) in product.images" 
              :key="index" 
              class="thumbnail"
              :class="{ active: currentImageIndex === index }"
              @click="setCurrentImage(index)"
            >
              <img :src="image" alt="Thumbnail" @error="handleThumbnailError">
            </div>
          </div>
        </div>

        <!-- 3. Product Info -->
        <div class="product-info">
          <!-- Product Description with Title and Quick Info -->
          <div class="product-description">
            <div class="product-title-container">
              <h1 class="product-title" :class="{ expanded: isTitleExpanded }">{{ cleanedDescription }}</h1>
              <button class="expand-button" @click="toggleTitleExpand">
                {{ isTitleExpanded ? 'Short' : 'Expand' }}
              </button>
            </div>
            <div class="product-quick-info">
              <div class="product-quick-info-left">
                <div class="product-price">
                  <span v-if="product.originalPrice" class="original-price" style="text-decoration: line-through; color: #000; margin-right: 8px; font-size: 0.9em;">{{ product.originalPrice }}</span>
                  <span :class="{'final-price-red': !!product.originalPrice}" :style="product.originalPrice ? { color: 'red', fontWeight: 'bold' } : {}">{{ product.price }}</span>
                </div>
                <div class="product-condition">{{ product.condition || 'New' }}</div>
                <div class="product-size" v-if="product.size">{{ product.size }}</div>
              </div>
              <button class="favourite-btn-inline favourite-btn-price" @click.stop.prevent="toggleFavouriteUrl(currentProductUrl)">
                <i
                  v-if="isFavouritedUrl(currentProductUrl)"
                  class="fas fa-heart favourite-heart is-active"
                  :class="{ popping: poppingFavouriteUrl === currentProductUrl }"
                ></i>
                <i v-else class="far fa-heart favourite-heart"></i>
              </button>
            </div>
            
            <!-- Move action buttons here -->
            <div class="action-buttons">
              <button class="action-button" @click="openContactModal">Contact Us</button>
              <button class="action-button" @click="addToCart">Add to Cart</button><!-- TODO: restore :disabled="isProductSoldOut" -->
              <!--<button class="action-button buy-now">Buy Now</button>-->
            </div>

            <!-- Admin Price Override -->
            <div v-if="isAdmin" class="admin-price-panel" style="margin-top: 20px; padding: 10px; border: 1px dashed red; background: #fff0f0;">
              <h4 style="margin: 0 0 10px 0; color: #d32f2f;">Admin: Override Price</h4>
              <div style="display: flex; gap: 10px;">
                <input v-model="newPriceInput" type="number" step="0.01" placeholder="Enter new numeric price" style="flex: 1; padding: 8px; border: 1px solid #ccc; border-radius: 4px;" />
                <button @click="updatePrice" :disabled="isUpdatingPrice" style="background: #d32f2f; color: white; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer; font-weight: bold;">
                  {{ isUpdatingPrice ? 'Updating...' : 'Change Price' }}
                </button>
              </div>
            </div>
          </div>
          
          <!-- Product Details -->
          <div class="item-main-info--ExVwW2NW">
            <div class="labels--ndhPFgp8" v-if="parsedSpecs.length">
              <div class="item--qI9ENIfp" v-for="(spec, index) in parsedSpecs" :key="index">
                <div class="label--ejJeaTRV">
                  {{ spec.label }}
                  <span class="symbol--DK_64UaK">:</span>
                </div>
                <div class="value--EyQBSInp">{{ spec.value }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Similar Items -->
      <div class="similar-items" v-if="!loadingSimilar && similarProducts.length">
        <h2>Similar Items</h2>
        <div class="similar-products">
          <div 
            v-for="(product, index) in similarProducts" 
            :key="`${product.text}-${product.price}-${index}`" 
            class="similar-product-card"
            @click="goToProductDetail(product)"
          >
            <div class="similar-product-image">
              <img :src="product.image" :alt="product.text" @error="handleSimilarImageError" loading="lazy" />
            </div>
            <div class="similar-product-details">
              <h3 class="similar-product-title">{{ product.text }}</h3>
              <div class="similar-product-price-row">
                <div v-if="product.originalPrice" class="price-container">
                   <p class="similar-product-price original-price" style="text-decoration: line-through; color: black; font-size: 0.9em; margin-right: 5px;">{{ product.originalPrice }}</p>
                   <p class="similar-product-price new-price" style="color: red; font-weight: bold;">{{ product.price }}</p>
                </div>
                <p v-else class="similar-product-price">{{ product.price }}</p>
                <button
                  class="favourite-btn"
                  @click.stop.prevent="toggleFavouriteUrl(getFavouriteUrl(product))"
                >
                  <i
                    v-if="isFavouritedUrl(getFavouriteUrl(product))"
                    class="fas fa-heart favourite-heart is-active"
                    :class="{ popping: poppingFavouriteUrl === getFavouriteUrl(product) }"
                  ></i>
                  <i v-else class="far fa-heart favourite-heart"></i>
                </button>
              </div>
              <div class="similar-seller-details">
                <div class="similar-seller-info">
                  <img 
                    :src="product.sellerImage" 
                    :alt="product.sellerName"
                    @error="handleSellerImageError"
                    class="similar-seller-image"
                    loading="lazy"
                  />
                  <span class="similar-seller-name">{{ product.sellerName }}</span>
                </div>
                <span class="similar-reviews-count" v-if="product.sellerReviews">{{ product.sellerReviews }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div v-else-if="loadingSimilar" class="similar-items-loading">
        <h2>Similar Items</h2>
        <div class="loading">
          <div class="spinner"></div>
          Loading similar products...
        </div>
      </div>
    </div>

    <!-- Add Contact Modal at the end of the main template -->
    <div v-if="showContactModal" class="contact-modal-overlay" @click.self="closeContactModal">
      <div class="contact-modal">
        <button class="close-modal" @click="closeContactModal">×</button>
        <h2 class="modal-title">Ask A Question</h2>
        <div v-if="isCheckingLogin" class="loading-message">
          <div class="spinner"></div>
          <p>Loading...</p>
        </div>
        <div v-else-if="!isLoggedIn" class="login-message">
          <p>Please log in to send a message</p>
          <button class="login-button" @click="goToLogin">Log In</button>
        </div>
        <template v-else>
          <div class="modal-label"><b>Send a message</b> <span class="modal-warning"></span></div>
          <textarea
            v-model="contactMessage"
            class="modal-textarea"
            placeholder="Send a message to request more details or discuss price."
            rows="5"
          ></textarea>
          <div class="modal-info">
            We ensure that the items we review are authentic. All purchases made on Curatoreden are eligible for protection.
          </div>
          <button class="modal-send" :disabled="!contactMessage.trim()" @click="sendContactMessage">SEND MESSAGE</button>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import tokenService from '../services/tokenService';
import config from '../config';
import favouritesService from '../services/favouritesService';
import itemStatusUtils from '../utils/itemStatus';

export default {
  name: 'ProductDetail',
  data() {
    return {
      product: null,
      loading: true,
      loadingSimilar: true,
      error: null,
      currentImageIndex: 0,
      similarProducts: [],
      isZoomed: false,
      isTransitioning: false,
      transitionTimeout: null,
      parsedSpecs: [],
      isTitleExpanded: false,
      showContactModal: false,
      contactMessage: '',
      isLoggedIn: false,
      isCheckingLogin: false, // Add this new state
      favouritesMap: {},
      poppingFavouriteUrl: null,
      newPriceInput: '',
      isUpdatingPrice: false,
      isAdmin: false,
    }
  },
  computed: {
    currentProductUrl() {
      const id = this.$route.query.id;
      const categoryId = this.$route.query.categoryId || 0;
      if (!id) return null;
      return favouritesService.normalizeFavouriteUrl(`https://www.goofish.com/item?id=${encodeURIComponent(id)}&categoryId=${encodeURIComponent(categoryId)}`);
    },
    mainImage() {
      if (!this.product || !this.product.images || this.product.images.length === 0) {
        return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDUwIiBoZWlnaHQ9IjQ1MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjBmMGYwIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIyNCIgZmlsbD0iIzY2NiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlPC90ZXh0Pjwvc3ZnPg==';
      }
      return this.product.images[this.currentImageIndex];
    },
    cleanedSellerReviews() {
      if (!this.product || !this.product.sellerReviews) return '';
      return this.product.sellerReviews.replace(/Xianyu/gi, '').trim();
    },
    canNavigatePrevious() {
      return this.product && 
             this.product.images && 
             this.product.images.length > 1 && 
             !this.isTransitioning;
    },
    canNavigateNext() {
      return this.product && 
             this.product.images && 
             this.product.images.length > 1 && 
             !this.isTransitioning;
    },
    isProductSoldOut() {
      return itemStatusUtils.isItemStatusSoldOut(this.product?.itemStatus);
    },
    
    cleanedDescription() {
      if (!this.product || !this.product.description) return '';
      
      const numberWords = '(?:\\d+|one|two|three|four|five|six|seven|eight|nine|ten|twenty|thirty|forty|fifty|sixty|seventy|eighty|ninety|hundred|thousand)';
      
      return this.product.description
        .replace(/\bExpand\b/g, '')
        // Remove number words or digits followed by yuan
        .replace(new RegExp(`\\b${numberWords}\\s*yuan\\b`, 'gi'), '')
        // Remove price ranges with number words or digits
        .replace(new RegExp(`\\b${numberWords}\\s*(?:yuan|元)\\s*[,，]?\\s*(?:(?:now|price|at)\\s+)?${numberWords}\\s*(?:yuan|元)\\b`, 'gi'), '')
        // Remove free shipping phrases
        .replace(/\b(?:free\s+shipping|shipping\s+free|free\s+delivery|delivery\s+free)\b/gi, '')
        // Remove remote areas exceptions
        .replace(/\b(?:not\s+for|except\s+for|excluding|except)(?:\s+the)?\s+remote\s+areas?\b/gi, '')
        .replace(/\bTaobao\b/g, '')
        .replace(/\s+/g, ' ')
        .trim();
    }
  },
  created() {
    this.checkUserRole();

    // Get product ID from URL params
    const id = this.$route.query.id;
    const categoryId = this.$route.query.categoryId;
    
    if (id) {
      console.log('Fetching product details for ID:', id, 'Category ID:', categoryId);
      this.fetchProductDetails(id, categoryId);
    } else {
      this.error = 'Product ID is missing';
      this.loading = false;
    }

    // Load favourites (session-based). Non-blocking.
    this.loadFavourites();

    // Keep favourites map in sync after login/pending apply.
    this.$root.$on('favourites-changed', this.loadFavourites);
  },
  watch: {
    '$route.query': {
      handler(newQuery) {
        if (newQuery.id) {
          // Reset state
          this.product = null;
          this.loading = true;
          this.loadingSimilar = true;
          this.error = null;
          this.currentImageIndex = 0;
          this.similarProducts = [];
          this.isZoomed = false;
          this.parsedSpecs = [];
          this.isTitleExpanded = false;
          console.log('Route query changed, fetching new product details for ID:', newQuery.id, 'Category ID:', newQuery.categoryId);
          // Fetch new data
          this.fetchProductDetails(newQuery.id, newQuery.categoryId);
          
          // Scroll to top
          window.scrollTo(0, 0);
        }
      },
      immediate: false
    }
  },
  methods: {
    getFavouriteUrl(product) {
      return favouritesService.getProductUrlFromProduct(product);
    },

    isFavouritedUrl(url) {
      return !!(url && this.favouritesMap && this.favouritesMap[url]);
    },

    async loadFavourites() {
      try {
        const list = await favouritesService.listFavourites();
        const nextMap = {};
        for (const fav of list) {
          if (fav && fav.url) nextMap[fav.url] = true;
        }
        this.favouritesMap = nextMap;
      } catch (err) {
        if (err && err.response && err.response.status === 401) return;
        console.warn('Failed to load favourites', err);
      }
    },

    async toggleFavouriteUrl(url) {
      if (!url) return;
      const currentlyFav = !!this.favouritesMap[url];
      try {
        this.poppingFavouriteUrl = currentlyFav ? null : url;
        if (currentlyFav) {
          await favouritesService.removeFavourite(url);
          this.$delete(this.favouritesMap, url);
        } else {
          await favouritesService.addFavourite(url);
          this.$set(this.favouritesMap, url, true);
        }
        setTimeout(() => {
          if (this.poppingFavouriteUrl === url) this.poppingFavouriteUrl = null;
        }, 220);
      } catch (err) {
        if (err && err.response && err.response.status === 401) {
          // User tried to favourite while not logged in; apply it after login/signup.
          if (!currentlyFav) favouritesService.setPendingFavourite(url);
          this.$root.$emit('open-login-modal');
          return;
        }
        this.$root.$emit('show-toast', {
          message: err?.response?.data?.error || err?.message || 'Failed to update favourite.',
          type: 'error'
        });
      }
    },
    async updatePrice() {
      if (!this.newPriceInput || !this.product || !this.product.id) return;
      
      this.isUpdatingPrice = true;
      try {
        // Use token service if available, but admin check relies on session/cookie usually
        // However simple axios post is fine if cookies are sent
        await axios.post('/api/item/update-price', {
          itemId: this.product.id,
          newPrice: this.newPriceInput,
          originalPrice: this.product.originalPrice || this.product.price // Keep existing original or set current as original
        }, {
           withCredentials: true 
        });
        
        // Refresh product details
        await this.fetchProductDetails(this.product.id, this.product.categoryId);
        this.newPriceInput = '';
        this.$root.$emit('show-toast', { message: 'Price updated successfully', type: 'success' });
      } catch (err) {
        console.error('Failed to update price:', err);
         this.$root.$emit('show-toast', { 
           message: err.response?.data?.error || 'Failed to update price', 
           type: 'error' 
         });
      } finally {
        this.isUpdatingPrice = false;
      }
    },

    async getAuthToken() {
      // Use the token service to get a valid API token
      return await tokenService.getApiToken();
    },

    retryCurrentProduct() {
      const id = this.$route.query.id;
      const categoryId = this.$route.query.categoryId || 0;

      if (!id) {
        this.error = 'Product ID is missing';
        this.loading = false;
        this.loadingSimilar = false;
        return;
      }

      return this.fetchProductDetails(id, categoryId);
    },
    
    async fetchProductDetails(id, categoryId) {
      if (!id || typeof id === 'object') {
        this.error = 'Product ID is missing';
        this.loading = false;
        this.loadingSimilar = false;
        return;
      }

      this.loading = true;
      this.loadingSimilar = true;
      this.error = null;
      console.log('Fetching product details for ID:', id, 'Category ID:', categoryId);
      try {
        // Use the executeAuthenticatedRequest method to handle token management automatically
        const response = await tokenService.executeAuthenticatedRequest(async (token) => {
          return await axios.post(`/api/item?id=${id}&categoryId=${categoryId || 0}`, {}, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
        });
        
        console.log('API response:', response.data);
        
        // Handle direct JSON response
        this.parseJsonResponse(response.data);
        
      } catch (error) {
        console.error('Error fetching product details:', error);
        this.error = 'Failed to fetch product details. Please try again.';
        this.loading = false;
        this.loadingSimilar = false;
      }
    },
    
    parseJsonResponse(responseData) {
      try {
        console.log('Processing JSON response:', responseData);

        // Process itemData if available
        if (responseData.itemData) {
          console.log('Processing itemData:', responseData.itemData);
          this.processItemData(responseData.itemData);
          this.loading = false;
        }
        
        // Process recommendedProducts if available
        if (responseData.recommendedProducts) {
          console.log('Processing recommendedProducts:', responseData.recommendedProducts);
          this.processSimilarProducts(responseData.recommendedProducts);
          this.loadingSimilar = false;
        }

        // If we haven't found recommended products, set up retry
        // if (this.loadingSimilar && !responseData.recommendedProducts) {
        //   console.log('No recommended products found, will retry');
        //   setTimeout(async () => {
        //     try {
        //       // Use the token service for the retry request
        //       const secondResponse = await tokenService.executeAuthenticatedRequest(async (token) => {
        //         return await axios.post(
        //           `/api/item?id=${id}&categoryId=${categoryId || 0}`,
        //           {},
        //           {
        //             headers: {
        //               'Authorization': `Bearer ${token}`,
        //               'Content-Type': 'application/json'
        //             }
        //           }
        //         );
        //       });

        //       // Try to parse the retry response
        //       this.parseJsonResponse(secondResponse.data);
        //     } catch (retryError) {
        //       console.error('Error in retry request:', retryError);
        //       this.loadingSimilar = false;
        //     }
        //   }, 3000);
        // }

      } catch (error) {
        console.error('Error processing JSON response:', error);
        this.error = 'Failed to process product data';
        this.loading = false;
        this.loadingSimilar = false;
      }
    },
    
    parseLabelData(specs) {
      if (!specs) return [];
      
      // Create a temporary div to parse HTML
      const temp = document.createElement('div');
      temp.innerHTML = specs;
      
      // Get all spec items
      const items = Array.from(temp.querySelectorAll('.labels--ndhPFgp8 .item--qI9ENIfp'));
      const result = [];
      
      items.forEach(item => {
        const labelEl = item.querySelector('.label--ejJeaTRV');
        const valueEl = item.querySelector('.value--EyQBSInp');
        
        if (labelEl && valueEl) {
          result.push({
            label: labelEl.textContent.replace(':', '').trim(),
            value: valueEl.textContent.trim()
          });
        }
      });
      
      return result;
    },
    
    processItemData(itemData) {
      try {
        // Extract item data
        const data = itemData.data || {};
        const itemStatus = itemStatusUtils.extractItemStatusFromItemApiResponse({ itemData });
        
        // Process seller info
        const seller = data.seller || {};
        
        // Extract seller ID from sellerUrl
        let sellerId = null;
        if (seller.sellerUrl) {
          try {
            const url = new URL(seller.sellerUrl);
            sellerId = url.searchParams.get('userId');
          } catch (e) {
            console.error('Error extracting seller ID from URL:', e);
          }
        }
        
        // Handle image sources properly - keep original URLs
        let images = [];
        if (data.imageSources && Array.isArray(data.imageSources)) {
          images = data.imageSources.map(img => {
            if (img && typeof img === 'string' && img.trim().length > 0) {
              const processedUrl = img.trim();
              return processedUrl;
            }
            return null;
          }).filter(img => img !== null);
        }
        
        // Default image placeholder if no images available
        if (images.length === 0) {
          images = ['data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDUwIiBoZWlnaHQ9IjQ1MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjBmMGYwIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIyNCIgZmlsbD0iIzY2NiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlPC90ZXh0Pjwvc3ZnPg=='];
        }
        
        // Process seller image - keep original URL
        let sellerImage = seller.sellerImage || null;
        if (!sellerImage) {
          sellerImage = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAiIGhlaWdodD0iMzAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMTUiIGN5PSIxNSIgcj0iMTUiIGZpbGw9IiNmMGYwZjAiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjE0IiBmaWxsPSIjNjY2IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+UzwvdGV4dD48L3N2Zz4=';
        }
        
        // Transform API data into our product object
        const productData = {
          id: this.$route.query.id,
          categoryId: this.$route.query.categoryId,
          title: data.description ? this.extractTitle(data.description) : 'Product Name',
          price: data.price || 'Price not available',
          originalPrice: data.originalPrice || null,
          itemStatus,
          images: images,
          view: data.view || '',
          description: this.cleanHtml(data.description || ''),
          specs: data.specs || '',
          sellerName: seller.sellerName || 'Seller',
          sellerImage: sellerImage,
          sellerReviews: seller.sellerInfo || '',
          sellerLocation: this.extractLocation(seller.sellerInfo || ''),
          sellerUrl: seller.sellerUrl || '',
          sellerId: sellerId,
          postDate: new Date().toISOString()
        };
        
        this.product = productData;
        
        // Parse specs and update parsedSpecs
        this.parsedSpecs = this.parseLabelData(data.specs);
        
        // Log the product data for debugging
        console.log('Processed product data:', this.product);
      } catch (error) {
        console.error('Error processing item data:', error);
        this.error = 'Failed to process product data';
        this.loading = false;
      }
    },
    
    processSimilarProducts(recommendedProducts) {
      const data = recommendedProducts.data || [];
      
      if (data && data.length) {
        this.similarProducts = data.map(product => {
          // Keep original image URLs
          const imageUrl = product.image && typeof product.image === 'string' ? product.image.trim() : null;
          const sellerImageUrl = product.sellerImage && typeof product.sellerImage === 'string' ? product.sellerImage.trim() : null;
          
          return {
            ...product,
            text: product.text || 'No title',
            price: product.price || 'Price not available',
            image: imageUrl || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDUwIiBoZWlnaHQ9IjQ1MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjBmMGYwIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIyNCIgZmlsbD0iIzY2NiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlPC90ZXh0Pjwvc3ZnPg==',
            sellerName: product.sellerName || 'Seller',
            sellerImage: sellerImageUrl || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAiIGhlaWdodD0iMzAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMTUiIGN5PSIxNSIgcj0iMTUiIGZpbGw9IiNmMGYwZjAiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjE0IiBmaWxsPSIjNjY2IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+UzwvdGV4dD48L3N2Zz4=',
            sellerReviews: product.sellerReviews || null,
            badge: null,
            productLink: product.productLink || null
          };
        });
      }
      
      console.log('Processed similar products:', this.similarProducts);
    },
    
    processProductData(data) {
      // Process itemData if available
      if (data.itemData) {
        this.processItemData(data.itemData);
      }
      
      // Process recommendedProducts if available
      if (data.recommendedProducts) {
        this.processSimilarProducts(data.recommendedProducts);
      }
    },
    
    // Helper methods to extract information from HTML data
    cleanHtml(html) {
      if (!html) return '';
      // Create a temporary element to parse the HTML
      const temp = document.createElement('div');
      temp.innerHTML = html;
      return temp.textContent || temp.innerText || '';
    },
    
    extractTitle(description) {
      // Extract first sentence or part of HTML description as title
      const cleaned = this.cleanHtml(description);
      const firstSentence = cleaned.split(/[.!?]/).filter(s => s.trim().length > 0)[0];
      return firstSentence || cleaned.substring(0, 100);
    },
    
    extractTags(specs) {
      // Extract tags from specs
      const text = this.cleanHtml(specs);
      const words = text.split(/[\s:]+/).filter(word => 
        word.length > 3 && 
        !['other', 'brand', 'item', 'mode', 'purchase', 'canal', 'road', 'clear', 'packaging'].includes(word.toLowerCase())
      );
      
      // Return unique words as tags
      return [...new Set(words)].slice(0, 5);
    },
    
    extractCondition(specs) {
      const text = this.cleanHtml(specs).toLowerCase();
      if (text.includes('brand new')) return 'Brand New';
      if (text.includes('new')) return 'New';
      if (text.includes('used')) return 'Used';
      return 'Not specified';
    },
    
    extractSize(specs) {
      const text = this.cleanHtml(specs).toLowerCase();
      const sizeMatch = text.match(/size\s*:\s*([^,\.]+)/i) || 
                       text.match(/ruler\s*inch\s*:\s*([^,\.]+)/i);
      return sizeMatch ? sizeMatch[1].trim() : null;
    },
    
    extractBrand(specs) {
      const text = this.cleanHtml(specs).toLowerCase();
      const brandMatch = text.match(/brand\s*:\s*([^,\.]+)/i);
      return brandMatch ? brandMatch[1].trim() : null;
    },
    
    extractColor(specs) {
      const text = this.cleanHtml(specs).toLowerCase();
      const colorMatch = text.match(/color\s*:\s*([^,\.]+)/i) ||
                        text.match(/become\s*color\s*:\s*([^,\.]+)/i);
      return colorMatch ? colorMatch[1].trim() : null;
    },
    
    extractStyle(specs) {
      const text = this.cleanHtml(specs).toLowerCase();
      const styleMatch = text.match(/style\s*:\s*([^,\.]+)/i) ||
                         text.match(/payment\s*mode\s*:\s*([^,\.]+)/i);
      return styleMatch ? styleMatch[1].trim() : null;
    },
    
    extractLocation(sellerInfo) {
      if (!sellerInfo) return null;
      const locationMatch = sellerInfo.match(/^([^|]+)\|/);
      return locationMatch ? locationMatch[1].trim() : null;
    },
    
    extractIdFromUrl(url) {
      if (!url) return null;
      
      try {
        // Handle URLs with different formats
        if (url.includes('?')) {
          // For URLs with query parameters like https://www.goofish.com/item?id=123&categoryId=456
          const idMatch = url.match(/id=([^&]+)/);
          return idMatch ? idMatch[1] : null;
        } else if (url.includes('/item/')) {
          // For URLs with path parameters like https://www.goofish.com/item/123/456
          const pathMatch = url.match(/\/item\/([^\/]+)/);
          return pathMatch ? pathMatch[1] : null;
        }
        return null;
      } catch (e) {
        console.error('Error extracting ID from URL:', e);
        return null;
      }
    },
    
    extractCategoryIdFromUrl(url) {
      if (!url) return null;
      
      try {
        // Handle URLs with different formats
        if (url.includes('?')) {
          // For URLs with query parameters
          const categoryMatch = url.match(/categoryId=([^&]+)/);
          // If categoryId is not found or is 0, use a default category
          const category = categoryMatch ? categoryMatch[1] : null;
          return category === '0' ? '126866685' : category;
        } else if (url.includes('/item/')) {
          // For URLs with path parameters like https://www.goofish.com/item/123/456
          const pathMatch = url.match(/\/item\/[^\/]+\/([^\/]+)/);
          const category = pathMatch ? pathMatch[1] : null;
          return category === '0' ? '126866685' : category;
        }
        // Default category if nothing found
        return '126866685';
      } catch (e) {
        console.error('Error extracting category ID from URL:', e);
        // Return a default category ID as fallback
        return '126866685';
      }
    },
    
    setCurrentImage(index) {
      if (this.product && this.product.images && index >= 0 && index < this.product.images.length) {
        this.currentImageIndex = index;
      }
    },
    
    formatDate(dateString) {
      if (!dateString) return 'Unknown date';
      
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        });
      } catch (e) {
        return 'Invalid date';
      }
    },
    
    formatTimeAgo(date) {
      if (!date) return '';
      
      const now = new Date();
      const past = new Date(date);
      const diffInSeconds = Math.floor((now - past) / 1000);
      
      if (diffInSeconds < 60) return 'just now';
      if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
      if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
      if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} days ago`;
      if (diffInSeconds < 31536000) return `${Math.floor(diffInSeconds / 2592000)} months ago`;
      return `${Math.floor(diffInSeconds / 31536000)} years ago`;
    },
    
    tryAlternativeImageFormats(imageElement, originalUrl) {
      if (!originalUrl || !imageElement) return;
      
      console.log('Trying alternative formats for:', originalUrl);
      
      // Create base URL by removing query parameters and file extension
      let baseUrl = originalUrl.split('?')[0];
      baseUrl = baseUrl.replace(/\.(webp|jpg|jpeg|png|gif)(_\d+x\d+)?$/, '');
      
      // Keep any size parameters from the original URL
      const sizeMatch = originalUrl.match(/_(\d+x\d+)/);
      const sizeParam = sizeMatch ? sizeMatch[1] : '';
      
      // Create an array of URLs to try
      const urlsToTry = [
        `${baseUrl}.jpg${sizeParam}`,
        `${baseUrl}_790x10000Q90.jpg`,
        `${baseUrl}_450x10000Q90.jpg`,
        `${baseUrl}.jpeg${sizeParam}`,
        `${baseUrl}.png${sizeParam}`,
        originalUrl // Try original URL as last resort
      ];
      
      let currentIndex = 0;
      
      const tryNextUrl = () => {
        if (currentIndex >= urlsToTry.length) {
          console.log('All URLs failed, using placeholder');
          imageElement.onerror = null;
          
          // Use appropriate placeholder based on element size
          if (imageElement.width <= 50) {
            imageElement.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAiIGhlaWdodD0iMzAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMTUiIGN5PSIxNSIgcj0iMTUiIGZpbGw9IiNmMGYwZjAiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjE0IiBmaWxsPSIjNjY2IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+UzwvdGV4dD48L3N2Zz4=';
          } else {
            imageElement.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDUwIiBoZWlnaHQ9IjQ1MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjBmMGYwIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIyNCIgZmlsbD0iIzY2NiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlPC90ZXh0Pjwvc3ZnPg==';
          }
          return;
        }
        
        const nextUrl = urlsToTry[currentIndex];
        console.log(`Trying URL ${currentIndex + 1}/${urlsToTry.length}:`, nextUrl);
        
        imageElement.onerror = () => {
          console.log(`Failed to load URL:`, nextUrl);
          currentIndex++;
          tryNextUrl();
        };
        
        imageElement.src = nextUrl;
      };
      
      tryNextUrl();
    },
    
    handleImageError(e) {
      console.log('Product detail image failed to load:', e.target.src);
      
      // Prevent recursion by checking if we've already tried alternative formats
      if (e.target.getAttribute('data-tried-alternative') === 'true') {
        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDUwIiBoZWlnaHQ9IjQ1MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjBmMGYwIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIyNCIgZmlsbD0iIzY2NiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlPC90ZXh0Pjwvc3ZnPg==';
        return;
      }
      
      // Mark that we've tried alternatives
      e.target.setAttribute('data-tried-alternative', 'true');
      
      // Try different image formats
      this.tryAlternativeImageFormats(e.target, e.target.src);
    },
    
    handleThumbnailError(e) {
      console.log('Thumbnail image failed to load:', e.target.src);
      
      // Prevent recursion by checking if we've already tried alternative formats
      if (e.target.getAttribute('data-tried-alternative') === 'true') {
        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iODAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTIiIGZpbGw9IiM2NjYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5ObyBJbWFnZTwvdGV4dD48L3N2Zz4=';
        return;
      }
      
      // Mark that we've tried alternatives
      e.target.setAttribute('data-tried-alternative', 'true');
      
      // Try different image formats
      this.tryAlternativeImageFormats(e.target, e.target.src);
    },
    
    handleSellerImageError(e) {
      console.log('Seller image failed to load:', e.target.src);
      
      // Prevent recursion by checking if we've already tried alternative formats
      if (e.target.getAttribute('data-tried-alternative') === 'true') {
        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAiIGhlaWdodD0iMzAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMTUiIGN5PSIxNSIgcj0iMTUiIGZpbGw9IiNmMGYwZjAiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjE0IiBmaWxsPSIjNjY2IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+UzwvdGV4dD48L3N2Zz4=';
        return;
      }
      
      // Mark that we've tried alternatives
      e.target.setAttribute('data-tried-alternative', 'true');
      
      // Try different image formats
      this.tryAlternativeImageFormats(e.target, e.target.src);
    },
    
    handleSimilarImageError(e) {
      console.log('Similar product image failed to load:', e.target.src);
      
      // Prevent recursion by checking if we've already tried alternative formats
      if (e.target.getAttribute('data-tried-alternative') === 'true') {
        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTUwIiBoZWlnaHQ9IjE1MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjBmMGYwIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzY2NiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlPC90ZXh0Pjwvc3ZnPg==';
        return;
      }
      
      // Mark that we've tried alternatives
      e.target.setAttribute('data-tried-alternative', 'true');
      
      // Try different image formats
      this.tryAlternativeImageFormats(e.target, e.target.src);
    },
    
    goToProductDetail(product) {
      // Log which product is being clicked
      console.log('Clicking product:', product);
      
      let id = null;
      
      // Try to extract id from productLink first
      if (product.productLink) {
        id = this.extractIdFromUrl(product.productLink);
      } 
      
      // If extraction failed or no link, try direct id
      if (!id && product.id) {
        id = product.id;
      }
      
      // Check if we have an id
      if (id) {
        // Navigate within the same tab (SPA routing)
        this.$router.push({ name: 'ProductDetail', query: { id: id, categoryId: 0 } });
      } else {
        console.warn('Unable to navigate to product detail: missing id');
      }
    },

    previousImage() {
      if (this.isTransitioning || !this.canNavigatePrevious) return;
      
      this.isTransitioning = true;
      const prevIndex = (this.currentImageIndex - 1 + this.product.images.length) % this.product.images.length;
      
      // Set a small delay before changing the image
      this.transitionTimeout = setTimeout(() => {
        this.currentImageIndex = prevIndex;
        
        // Reset transition state after animation
        setTimeout(() => {
          this.isTransitioning = false;
        }, 300); // Match the CSS transition duration
      }, 50);
    },

    nextImage() {
      if (this.isTransitioning || !this.canNavigateNext) return;
      
      this.isTransitioning = true;
      const nextIndex = (this.currentImageIndex + 1) % this.product.images.length;
      
      // Set a small delay before changing the image
      this.transitionTimeout = setTimeout(() => {
        this.currentImageIndex = nextIndex;
        
        // Reset transition state after animation
        setTimeout(() => {
          this.isTransitioning = false;
        }, 300); // Match the CSS transition duration
      }, 50);
    },

    toggleZoom(event) {
      this.isZoomed = !this.isZoomed;
      
      // Prevent scrolling when zoomed
      document.body.style.overflow = this.isZoomed ? 'hidden' : '';
      
      // Set header z-index to -1 when zoomed, restore when unzoomed
      const header = document.querySelector('header');
      if (header) {
        header.style.zIndex = this.isZoomed ? '-1' : '1001';
      }
      
      // If closing zoom with Escape key
      if (!this.isZoomed && !event) {
        return;
      }
      
      event.stopPropagation();
    },
    
    // Add keyboard navigation
    handleKeydown(event) {
      if (this.isZoomed) {
        switch(event.key) {
          case 'Escape':
            this.toggleZoom();
            break;
          case 'ArrowLeft':
            this.previousImage();
            break;
          case 'ArrowRight':
            this.nextImage();
            break;
        }
      }
    },
    


    addToCart() {
      if (!this.product) return;

      // TODO: restore sold-out guard below
      // if (this.isProductSoldOut) {
      //   this.$root.$emit('show-toast', {
      //     message: 'Sold out items cannot be added to cart',
      //     type: 'warning'
      //   });
      //   return;
      // }
      
      // Get the current URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const productId = urlParams.get('id');
      const categoryId = urlParams.get('categoryId');
      
      // Check if product is already in cart
      const cartItems = this.$store.state.cart || [];
      const isProductInCart = cartItems.find(item => 
        item.id === this.product.id
      );

      if (isProductInCart) {
        // Show warning message if product already in cart
        this.$root.$emit('show-toast', {
          message: 'This item is already in your cart',
          type: 'warning'
        });
        return;
      }
      
      const cartItem = {
        id: this.product.id,
        title: this.product.title,
        price: this.product.price,
        image: this.product.images[0],
        sellerName: this.product.sellerName,
        url: window.location.search, // Store the full URL query string
        productId: productId,
        categoryId: categoryId
      };
      
      this.$store.commit('ADD_TO_CART', cartItem);
      
      // Show success message
      this.$root.$emit('show-toast', {
        message: 'Item added to cart',
        type: 'success'
      });
    },

    formatSpecsArray(specs) {
      if (!specs) return [];
      
      // Split by <br> or newline and process each line
      const lines = specs.split(/(?:<br\s*\/?>|\n)/);
      return lines
        .map(line => {
          // Skip empty lines
          if (!line.trim()) return null;
          
          // Find the position of the colon
          const colonIndex = line.indexOf(':');
          if (colonIndex > -1) {
            return {
              label: line.substring(0, colonIndex).trim(),
              value: line.substring(colonIndex + 1).trim()
            };
          }
          return null;
        })
        .filter(item => item !== null); // Remove null items
    },

    toggleTitleExpand() {
      this.isTitleExpanded = !this.isTitleExpanded;
    },

    async checkUserRole() {
      try {
        console.log('Checking user role...');
        const response = await axios.get(`${config.apiBaseUrl}/api/auth/check-session`, {
          withCredentials: true
        });
        console.log('User session data response:', response.data);
        this.isAdmin = response.data.user?.role === 'admin';
        console.log('Is Admin:', this.isAdmin);
      } catch (error) {
        console.error('Check user role error:', error);
        this.isAdmin = false;
      }
    },

    async openContactModal() {
      this.isCheckingLogin = true; // Set checking state to true
      this.showContactModal = true;
      this.contactMessage = '';
      
      try {
        const response = await axios.get(`${config.apiBaseUrl}/api/auth/check-session`, { withCredentials: true });
        this.isLoggedIn = !!response.data.user;
      } catch (error) {
        console.error('Error checking login status:', error);
        this.isLoggedIn = false;
      } finally {
        this.isCheckingLogin = false; // Set checking state to false when done
      }
    },
    closeContactModal() {
      this.showContactModal = false;
      this.isLoggedIn = false;
    },
    goToLogin() {
      this.closeContactModal();
      // Wait for modal to close before clicking login button
      //setTimeout(() => {
        const loginButton = document.querySelector('.login-btn');
        if (loginButton) {
          loginButton.click();
        }
      //}, 100); // Small delay to ensure modal is closed
    },
    async sendContactMessage() {
      try {
        const message = this.contactMessage.trim();
        if (!message) return;
        const productId = this.product?.id;
        const categoryId = this.product?.categoryId;
        const productTitle = this.product?.title || '';
        const productImage = this.product?.images?.[0] || '';
        const shortTitle = productTitle.length > 60 ? productTitle.slice(0, 60) + '...' : productTitle;
        
        await axios.post('/api/contact', { message, productId, categoryId, productTitle: shortTitle, productImage });
        
        this.$root.$emit('show-toast', {
          message: 'Your message has been sent!',
          type: 'success'
        });
        this.closeContactModal();
      } catch (error) {
        console.error('Error sending message:', error);
        this.$root.$emit('show-toast', {
          message: 'Failed to send message. Please try again later.',
          type: 'error'
        });
      }
    },
  },
  mounted() {
    window.addEventListener('keydown', this.handleKeydown);
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.handleKeydown);
    document.body.style.overflow = '';

    this.$root.$off('favourites-changed', this.loadFavourites);
    
    // Restore header z-index
    const header = document.querySelector('header');
    if (header) {
      header.style.zIndex = '1001';
    }
    
    if (this.transitionTimeout) {
      clearTimeout(this.transitionTimeout);
    }
  }
}
</script>

<style scoped>
.product-detail-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  /*
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  */
}

.loading {
  text-align: center;
  padding: 40px;
  color: #666;
}

.spinner {
  width: 40px;
  height: 40px;
  margin: 0 auto 20px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error {
  color: #721c24;
  background: #f8d7da;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 20px;
  text-align: center;
}

.product-title-container {
  position: relative;
}

.favourite-btn-inline {
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 6px;
  line-height: 1;
}

.favourite-btn-price {
  margin-left: auto;
  padding: 0;
}

.similar-product-card {
  position: relative;
}

.favourite-btn {
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  line-height: 1;
}

.similar-product-price-row {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.similar-product-price-row .favourite-btn {
  margin-left: auto;
}

.favourite-heart {
  font-size: 18px;
}

.favourite-heart.is-active {
  color: #ff4444;
}

.favourite-heart.popping {
  animation: favPop 200ms ease-out;
}

@keyframes favPop {
  0% { transform: scale(0.85); }
  60% { transform: scale(1.15); }
  100% { transform: scale(1.0); }
}

.retry-button {
  margin-top: 15px;
  padding: 10px 20px;
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s;
}

.retry-button:hover {
  background: #c82333;
}

/* Main Content Area */

/* Add responsive styles for product content */
@media (max-width: 900px) {
  .product-content {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  
  .product-info {
    /*order: -1;*/
     /* Move product info above images on mobile */
  }
  
  .product-title {
    font-size: 16px;
  }
  
  .product-price {
    font-size: 20px;
  }
  
  .action-buttons {
    flex-direction: column;
    gap: 8px;
  }
  
  .action-button {
    width: 100%;
  }
}

@media (max-width: 600px) {
  .product-detail-page {
    padding: 10px;
  }
  
  .seller-info-top {
    padding: 8px 12px;
  }
  
  .seller-details {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }
  
  .seller-name-container {
    flex: 1;
    min-width: 0; /* Allows text truncation to work */
  }
  
  .seller-name {
    font-size: 14px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  .seller-stats {
    font-size: 12px;
  }
  
  .seller-avatar {
    width: 32px;
    height: 32px;
  }
  
  .main-image {
    border-radius: 0;
  }
  
  .thumbnail {
    width: 60px;
    height: 60px;
  }
  
  .nav-button {
    width: 32px;
    height: 32px;
    font-size: 20px;
  }
}

/* Images Gallery */
.product-images {
  display: flex;
  flex-direction: column;
}

.main-image {
  position: relative;
  width: auto; /*90%;*/
  height: 0;
  padding-bottom: 100%;
  /*background: #f8f8f8;*/
  overflow: hidden;
  cursor: zoom-in;
}

.main-image.zoomed {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  padding: 0;
  background: rgba(0, 0, 0, 0.9);
  z-index: 9999; /* Increased z-index to ensure it's above everything */
  cursor: zoom-out;
  display: flex;
  align-items: center;
  justify-content: center;
}

.main-image.zoomed img {
  max-width: 90%;
  max-height: 90%;
  width: auto;
  height: auto;
  position: relative;
  object-fit: contain;
  transition: transform 0.3s ease;
  z-index: 10000; /* Ensure image is above the background */
}

.main-image img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  /*height: 100%;*/
  object-fit: contain;
  object-position: center;
  transition: all 0.3s ease;
}

.main-image.is-sold-out img {
  filter: grayscale(100%) saturate(0.15) contrast(0.92);
  transform: scale(1.01);
}

.main-image.is-sold-out::after {
  content: '';
  position: absolute;
  inset: 0;
  background:
    linear-gradient(180deg, rgba(71, 78, 90, 0.42), rgba(60, 67, 80, 0.5)),
    repeating-linear-gradient(
      -45deg,
      rgba(238, 241, 247, 0.08) 0,
      rgba(238, 241, 247, 0.08) 4px,
      rgba(86, 94, 110, 0.08) 4px,
      rgba(86, 94, 110, 0.08) 8px
    );
  pointer-events: none;
  z-index: 1;
}

.sold-out-badge {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 3;
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 800;
  color: #2f3748;
  background: rgba(230, 233, 239, 0.9);
  border: 1px solid rgba(126, 136, 153, 0.78);
  box-shadow: 0 6px 12px rgba(41, 48, 63, 0.22);
  backdrop-filter: blur(1px);
  pointer-events: none;
  white-space: nowrap;
}

.image-thumbnails {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding: 5px 0;
}

.thumbnail {
  /*width: 80px;
  height: 80px;*/
  width: auto;
  height: auto;
  overflow: hidden;
  cursor: pointer;
  opacity: 0.7;
  transition: all 0.2s;
  background: #f8f8f8;
}

.thumbnail:hover {
  opacity: 0.9;
}

.thumbnail.active {
  border-color: #3498db;
  opacity: 1;
}

.thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Product Info */
.product-info {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

/* Seller Info */
.seller-info-top {
  padding: 10px 20px;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.seller-details {
  display: grid;
  flex-direction: column;
}

.seller-name-container {
  display: flex;
  align-items: center;
  gap: 8px;
}

.seller-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
}

.seller-name-link {
  text-decoration: none;
  color: inherit;
}

.seller-name-link:hover .seller-name {
  color: #e44d26;
}

.seller-name {
  font-size: 16px;
  font-weight: 500;
  color: #333;
  margin: 0;
}

.seller-stats {
  display: flex;
  align-items: center;
  color: #666;
  font-size: 14px;
}

.nav-button {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  font-size: 24px;
  cursor: pointer;
  z-index: 1001;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 2px 6px rgba(0,0,0,0.1);
}

.nav-button:hover {
  background: rgba(255, 255, 255, 1);
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
}

.nav-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.prev {
  left: 10px;
}

.next {
  right: 10px;
}

.zoomed .nav-button {
  background: rgba(255, 255, 255, 0.15);
  color: white;
  width: 50px;
  height: 50px;
  font-size: 30px;
  z-index: 10001; /* Ensure buttons are above the zoomed image */
}

.zoomed .prev {
  left: 20px;
}

.zoomed .next {
  right: 20px;
}

.zoomed .nav-button:hover {
  background: rgba(255, 255, 255, 0.25);
}

.close-zoom {
  position: absolute;
  top: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.15);
  border: none;
  border-radius: 50%;
  color: white;
  font-size: 24px;
  cursor: pointer;
  z-index: 10001; /* Ensure close button is above the zoomed image */
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.close-zoom:hover {
  background: rgba(255, 255, 255, 0.25);
}

/* Product Description */
.product-description {
  margin-top: 0;
  background: white;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  padding: 0 10px;
}

.product-title-container {
  position: relative;
  margin-bottom: 15px;
  width: 100%;
}

.product-title {
  font-size: 17px;
  font-weight: 600;
  color: #111;
  line-height: 1.5;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  transition: all 0.3s ease;
  padding-right: 60px; /* Space for expand button */
}

.product-title.expanded {
  -webkit-line-clamp: unset;
  max-height: none;
}

.expand-button {
  position: absolute;
  right: 0;
  bottom: 2px;
  background: none;
  border: none;
  color: #3498db;
  font-size: 14px;
  cursor: pointer;
  padding: 2px 5px;
  font-weight: 500;
  display: none; /* Hide by default */
}

/* Only show expand button when text is truncated */
.product-title:not(.expanded) {
  display: -webkit-box;
}

.product-title:not(.expanded) + .expand-button,
.product-title.expanded + .expand-button {
  display: inline-block;
}

@media (max-width: 768px) {
  .product-title {
    font-size: 15px;
    line-height: 1.4;
    margin-bottom: 12px;
    padding-right: 50px;
  }

  .expand-button {
    font-size: 13px;
    bottom: 1px;
  }
}

@media (max-width: 480px) {
  .product-title {
    font-size: 14px;
    line-height: 1.3;
    margin-bottom: 10px;
    padding-right: 45px;
  }

  .expand-button {
    font-size: 12px;
    padding: 2px 4px;
  }
}

@media (max-width: 380px) {
  .product-title {
    font-size: 13px;
    line-height: 1.3;
    margin-bottom: 8px;
    padding-right: 40px;
  }

  .expand-button {
    font-size: 11px;
    padding: 2px 3px;
    bottom: 0;
  }
}

.product-quick-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 5px;
  width: 100%;
  max-width: 100%;
  flex-wrap: nowrap;
}

.product-quick-info-left {
  display: flex;
  align-items: center;
  gap: 15px;
  flex-wrap: wrap;
  min-width: 0;
}

.product-price {
  font-size: 22px;
  font-weight: bold;
  color: #e44d26;
}

.product-condition, .product-size {
  font-size: 14px;
  color: #666;
  padding: 4px 8px;
  background: #f5f5f5;
  border-radius: 4px;
}

/* Product Details */

.item-main-info--ExVwW2NW .labels--ndhPFgp8 {
  display: flex;
  flex-wrap: wrap;
  height: auto;
  justify-content: space-between;
  width: 100%;
}

.labels--ndhPFgp8 > .item--qI9ENIfp {
  display: flex;
  font-size: 14px;
  font-weight: 400;
  height: 14px;
  line-height: 14px;
  margin-bottom: 20px;
  width: 320px;
}

.item--qI9ENIfp .label--ejJeaTRV {
  color: #999;
  display: flex;
  justify-content: space-between;
}

.item--qI9ENIfp .symbol--DK_64UaK {
  color: #999;
  display: flex;
  width: 14px;
}

.item--qI9ENIfp > .value--EyQBSInp {
  color: #1f1f1f;
  display: inline-block;
  max-width: 170px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Similar Items */
.similar-items {
  margin-top: 40px;
}

.similar-items h2 {
  font-size: 22px;
  font-weight: 600;
  margin-bottom: 20px;
  color: #111;
}

.similar-products {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 20px;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 8px;
}

.similar-product-card {
  overflow: hidden;
  background: white;
  animation: fadeIn 0.3s ease-in;
  /*display: flex;*/
  flex-direction: column;
  /*height: 430px;*/
  height: auto;
  position: relative;
  cursor: pointer;
}

.similar-product-image {
  position: relative;
  width: 100%;
  height: 18rem;
  background: #f8f8f8;
}

.similar-product-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

.similar-product-details {
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.similar-product-title {
  margin: 0;
  font-size: 0.9rem;
  color: #333;
  font-weight: 500;
  line-height: 1.3;
  height: 2.8em;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
}

.similar-product-price {
  font-size: 1.1rem;
  color: #e44d26;
  font-weight: bold;
  margin: 0;
}

.similar-seller-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 8px;
}

.similar-seller-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.similar-seller-image {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.similar-seller-name {
  color: #666;
  font-size: 0.85rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
}

.similar-reviews-count {
  font-size: 0.8rem;
  color: #888;
  white-space: nowrap;
  margin-left: 32px;
}

/* Update responsive breakpoints for similar products */
@media (max-width: 1200px) {
  .similar-products {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 900px) {
  .similar-products {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 370px) {
  .similar-products {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
    padding: 0 4px;
  }
  
  .similar-product-card {
    height: auto;
    min-height: 220px;
  }
  
  .similar-product-details {
    padding: 8px;
  }
  
  .similar-product-title {
    font-size: 0.8rem;
    height: 2.7em;
    /*height: 2.4em;*/
  }
  
  .similar-product-price {
    font-size: 0.9rem;
  }
  
  .similar-seller-image {
    width: 20px;
    height: 20px;
  }
  
  .similar-seller-name {
    font-size: 0.75rem;
  }
  
  .similar-reviews-count {
    font-size: 0.7rem;
    margin-left: 24px;
  }
}

/* Add touch-friendly styles for mobile */
@media (max-width: 600px) {
  .nav-button {
    width: 40px;
    height: 40px;
    font-size: 24px;
  }
  
  .thumbnail {
    width: 70px;
    height: 70px;
  }
  
  .action-button {
    padding: 14px;
    font-size: 16px;
  }
}

/* Add lazy loading animation */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.similar-items-loading {
  margin-top: 40px;
}

.similar-items-loading h2 {
  font-size: 22px;
  font-weight: 600;
  margin-bottom: 20px;
  color: #111;
}

.similar-items-loading .loading {
  background: white;
  border-radius: 8px;
  padding: 40px;
  text-align: center;
  border: 1px solid #eee;
}

.similar-items-loading .spinner {
  width: 30px;
  height: 30px;
  margin: 0 auto 15px;
  border-width: 2px;
}

.action-buttons {
  display: flex;
  gap: 10px;
  margin: 20px 0;
}

.action-button {
  flex: 1;
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: #fff;
  color: #333;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
}

.action-button:hover {
  background: #f5f5f5;
}

.action-button:disabled {
  background: #e5e5e5;
  color: #888;
  border-color: #d6d6d6;
  cursor: not-allowed;
}

.action-button:disabled:hover {
  background: #e5e5e5;
}

.buy-now {
  background: #333;
  color: #fff;
  border: none;
}

.buy-now:hover {
  background: #000;
}

.seller-details-link {
  text-decoration: none;
  color: inherit;
  display: block;
  width: 100%;
  transition: background-color 0.2s;
}

.seller-details-link:hover {
  background-color: rgba(0, 0, 0, 0.02);
}

.seller-details-link:hover .seller-name {
  /*color: #e44d26;*/
}

@media (max-width: 768px) {
  .image-thumbnails {
    display: none;
  }
  
  .product-description {
    padding: 0 8px;
  }
  
  .seller-details {
    display: block;
  }
  
  .seller-name-container {
    margin-bottom: 4px;
  }
  
  .seller-stats {
    font-size: 12px;
  }

  .product-title {
    font-size: 15px;
    line-height: 1.4;
    margin-bottom: 12px;
    -webkit-line-clamp: 2;
  }
}

@media (max-width: 760px) {
  .image-thumbnails {
    display: none;
  }
  
  .product-description {
    padding: 0 8px;
  }
  
  .seller-details {
    display: block;
  }
  
  .seller-name-container {
    margin-bottom: 4px;
  }
  
  .seller-stats {
    font-size: 12px;
  }

  .product-title {
    font-size: 15px;
    line-height: 1.4;
    margin-bottom: 12px;
    -webkit-line-clamp: 2;
  }
}

@media (max-width: 480px) {
  .image-thumbnails {
    display: none;
  }
  
  .product-description {
    padding: 0 5px;
  }
  
  .seller-details {
    display: block;
  }
  
  .seller-name-container {
    margin-bottom: 4px;
  }
  
  .seller-stats {
    font-size: 12px;
  }

  .product-title {
    font-size: 14px;
    line-height: 1.3;
    margin-bottom: 10px;
  }
}

@media (max-width: 470px) {
  .image-thumbnails {
    display: none;
  }
  
  .product-description {
    padding: 0 5px;
  }
  
  .seller-details {
    display: block;
  }
  
  .seller-name-container {
    margin-bottom: 4px;
  }
  
  .seller-stats {
    font-size: 12px;
  }

  .product-title {
    font-size: 14px;
    line-height: 1.3;
    margin-bottom: 10px;
  }

  .similar-product-card {
    height: auto;
  }  
  
  .similar-product-image {
    height: 11rem;
  }
    
}

@media (max-width: 380px) {

  .similar-product-image {
    height: 11rem;
  }
  
  .image-thumbnails {
    display: none;
  }
  
  .product-description {
    padding: 0 4px;
  }
  
  .seller-details {
    display: block;
  }
  
  .seller-name-container {
    margin-bottom: 4px;
  }
  
  .seller-stats {
    font-size: 12px;
  }

  .product-title {
    font-size: 13px;
    line-height: 1.3;
    margin-bottom: 8px;
  }

  .expand-button {
    font-size: 12px;
  }
}

@media (min-width: 760px) {
  .seller-details {
    display: grid;
  }
  
  .product-title {
    -webkit-line-clamp: 3;
    width: 19rem;
  }
  
  .image-thumbnails {
    display: flex;
  }
}

.product-container {
  display: flex;
  flex-direction: column;
}

.product-content {
  display: grid;
  grid-template-columns: 1fr 350px;
  gap: 30px;
  margin-bottom: 40px;
}

@media (max-width: 768px) {
  .product-content {
    display: grid;
    flex-direction: column;
    gap: 20px;
  }

  .product-images {
    order: 2;
  }

  .product-info {
    order: 3;
  }

  .seller-info-top {
    order: 1;
  }
}

@media (max-width: 760px) {
  .product-content {
    display: grid;
    flex-direction: column;
    gap: 20px;
  }

  .product-images {
    order: 2;
  }

  .product-info {
    order: 3;
  }

  .seller-info-top {
    order: 1;
  }
}

@media (max-width: 480px) {
  .product-content {
    display: grid;
    flex-direction: column;
    gap: 15px;
  }

  .product-images {
    order: 2;
  }

  .product-info {
    order: 3;
  }

  .seller-info-top {
    order: 1;
  }
}

@media (max-width: 470px) {
  .product-content {
    display: grid;
    flex-direction: column;
    gap: 15px;
  }

  .product-images {
    order: 2;
  }

  .product-info {
    order: 3;
  }

  .seller-info-top {
    order: 1;
  }
}

@media (max-width: 380px) {
  .product-content {
    display: grid;
    flex-direction: column;
    gap: 12px;
  }

  .product-images {
    order: 2;
  }

  .product-info {
    order: 3;
  }

  .seller-info-top {
    order: 1;
  }
}

/* Add new layout styles */
.desktop-layout {
  display: block;
}

.mobile-layout {
  display: none;
}

@media (min-width: 760px) {
  .similar-products {
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
  }
}

@media (max-width: 768px) {

  .desktop-layout {
    display: none;
  }

  .mobile-layout {
    display: block;
  }

  .mobile-layout .product-images {
    margin-bottom: 24px;
    padding-bottom: 20px;
    border-bottom: 1px solid #eee;
  }
}

@media (max-width: 760px) {
  .desktop-layout {
    display: none;
  }

  .mobile-layout {
    display: block;
  }

  .mobile-layout .product-images {
    margin-bottom: 24px;
    padding-bottom: 20px;
    border-bottom: 1px solid #eee;
  }
}

@media (max-width: 480px) {
  .desktop-layout {
    display: none;
  }

  .mobile-layout {
    display: block;
  }

  .mobile-layout .product-images {
    margin-bottom: 20px;
    padding-bottom: 16px;
    border-bottom: 1px solid #eee;
  }
}

@media (max-width: 470px) {
  .desktop-layout {
    display: none;
  }

  .mobile-layout {
    display: block;
  }

  .mobile-layout .product-images {
    margin-bottom: 20px;
    padding-bottom: 16px;
    border-bottom: 1px solid #eee;
  }
}

@media (max-width: 380px) {
  .desktop-layout {
    display: none;
  }

  .mobile-layout {
    display: block;
  }

  .mobile-layout .product-images {
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid #eee;
  }
}

/* Add these styles */
.seller-link {
  display: flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  color: inherit;
}

.seller-link:hover .seller-name {
  color: #e44d26;
}

.seller-name-container {
  display: flex;
  align-items: center;
  gap: 8px;
}

.contact-modal-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.35);
  z-index: 20000;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Inter', 'Segoe UI', Arial, sans-serif;
}
.contact-modal {
  background: #fff;
  border-radius: 0;
  box-shadow: none;
  padding: 48px 40px 32px 40px;
  max-width: 600px;
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  font-family: 'Inter', 'Segoe UI', Arial, sans-serif;
}
.close-modal {
  position: absolute;
  top: 18px;
  right: 18px;
  background: none;
  border: none;
  font-size: 28px;
  color: #222;
  cursor: pointer;
  z-index: 1;
  font-family: inherit;
}
.modal-title {
  text-align: center;
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 18px;
  letter-spacing: 0.01em;
  font-family: inherit;
}
.modal-label {
  font-size: 1.08rem;
  margin-bottom: 8px;
  color: #222;
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
  font-family: inherit;
}
.modal-warning {
  font-size: 1.1rem;
}
.modal-textarea {
  width: 100%;
  min-height: 150px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 16px;
  font-size: 1rem;
  margin-bottom: 24px;
  resize: vertical;
  outline: none;
  transition: border 0.2s;
  font-family: inherit;
  background: #fafbfc;
}
.modal-textarea:focus {
  border: 1.5px solid #3498db;
}
.modal-info {
  font-size: 12px;
  color: #666;
  margin-bottom: 18px;
  display: flex;
  align-items: center;
  gap: 7px;
  flex-wrap: wrap;
  font-family: inherit;
}
.modal-lock {
  font-size: 1.1rem;
}
.modal-link {
  color: #3498db;
  text-decoration: underline;
  margin-left: 4px;
  font-family: inherit;
}
.modal-send {
  width: 100%;
  background: #222;
  color: #fff;
  border: none;
  border-radius: 5px;
  padding: 13px 0;
  font-size: 1.08rem;
  font-weight: 600;
  cursor: pointer;
  margin-top: 2px;
  transition: background 0.2s;
  font-family: inherit;
  letter-spacing: 0.01em;
}
.modal-send:enabled {
  background: #222;
  color: #fff;
}
.modal-send:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.login-message {
  text-align: center;
  padding: 2rem 0;
}

.login-message p {
  font-size: 1.1rem;
  color: #666;
  margin-bottom: 1.5rem;
}

.login-button {
  background: #4CAF50;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.8rem 2rem;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.login-button:hover {
  background: #45a049;
}

.loading-message {
  text-align: center;
  padding: 2rem 0;
}

.loading-message p {
  font-size: 1.1rem;
  color: #666;
  margin-top: 1rem;
}

.loading-message .spinner {
  width: 30px;
  height: 30px;
  margin: 0 auto;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style> 